// src/pages/Admin/Dashboard/components/ReglementFormModal.jsx

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FaBuilding,
  FaCalendarAlt,
  FaCreditCard,
  FaFileAlt,
  FaLink,
  FaMapMarkerAlt,
  FaSearch,
  FaTimes,
} from "react-icons/fa";

import {
  dateTimeLocalToIso,
  FORMULE_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
  PAYMENT_STATUS_OPTIONS,
  REGLEMENT_STATUS_OPTIONS,
  toDateTimeLocal,
} from "../utils/dashboard.utils";

const DEVIS_STATUS_OPTIONS = [
  {
    value: "non_disponible",
    label: "Non disponible",
  },
  {
    value: "a_signer",
    label: "À signer",
  },
  {
    value: "signe",
    label: "Signé",
  },
  {
    value: "annule",
    label: "Annulé",
  },
];

const emptyForm = {
  user_achat_id: "",
  formule: "",
  prix_total: "",
  date_achat: "",
  date_echeance: "",
  statut_reglement: "Réglé",
  mode_reglement: "Virement bancaire",
  payment_status: "",
  note: "",
  devis_url: "",
  devis_statut: "non_disponible",
  facture_url: "",
};

function hasOwn(object, key) {
  return Object.prototype.hasOwnProperty.call(
    object,
    key,
  );
}

function getUserLabel(user, club) {
  const fullName =
    `${user?.prenom || ""} ${user?.nom || ""}`.trim();

  const parts = [
    fullName || `Utilisateur #${user?.id}`,
  ];

  if (user?.email) {
    parts.push(user.email);
  }

  if (club?.nom_club) {
    parts.push(club.nom_club);
  }

  return parts.join(" — ");
}

function addOneYearToLocalDateTime(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  date.setFullYear(date.getFullYear() + 1);

  return toDateTimeLocal(date);
}

function isValidHttpUrl(value) {
  if (!value) return true;

  if (value.startsWith("/")) {
    return true;
  }

  try {
    const url = new URL(value);

    return (
      url.protocol === "http:" ||
      url.protocol === "https:"
    );
  } catch (error) {
    return false;
  }
}

export default function ReglementFormModal({
  open,
  reglement,
  users = [],
  clubsByUser = {},
  usersLoading = false,
  saving = false,
  onLoadUserClub,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState(emptyForm);
  const [userSearch, setUserSearch] = useState("");
  const [formError, setFormError] = useState("");
  const [clubLoading, setClubLoading] =
    useState(false);

  const [expiryManuallyChanged, setExpiryManuallyChanged] =
    useState(false);

  const isEditing = Boolean(reglement?.id);

  const selectedUserId = Number(
    form.user_achat_id,
  );

  const selectedUserKey =
    selectedUserId > 0
      ? String(selectedUserId)
      : "";

  const selectedClubLoaded =
    Boolean(selectedUserKey) &&
    hasOwn(clubsByUser, selectedUserKey);

  const selectedClub = selectedClubLoaded
    ? clubsByUser[selectedUserKey]
    : undefined;

  useEffect(() => {
    if (!open) return;

    setFormError("");
    setUserSearch("");
    setClubLoading(false);

    if (reglement) {
      const purchaseDate = toDateTimeLocal(
        reglement.date_achat,
      );

      const expiryDate = reglement.date_echeance
        ? toDateTimeLocal(reglement.date_echeance)
        : addOneYearToLocalDateTime(purchaseDate);

      setForm({
        user_achat_id: String(
          reglement.user_achat_id || "",
        ),

        formule: reglement.formule || "",

        prix_total:
          reglement.prix_total !== null &&
          reglement.prix_total !== undefined
            ? String(reglement.prix_total)
            : "",

        date_achat: purchaseDate,
        date_echeance: expiryDate,

        statut_reglement:
          reglement.statut_reglement || "Réglé",

        mode_reglement:
          reglement.mode_reglement ||
          (reglement.payment_status
            ? "Stripe"
            : "Autre"),

        payment_status:
          reglement.payment_status || "",

        note: reglement.note || "",

        devis_url: reglement.devis_url || "",

        devis_statut:
          reglement.devis_statut ||
          (reglement.devis_url
            ? "a_signer"
            : "non_disponible"),

        facture_url:
          reglement.facture_url || "",
      });

      setExpiryManuallyChanged(
        Boolean(reglement.date_echeance),
      );

      return;
    }

    const purchaseDate = toDateTimeLocal(
      new Date(),
    );

    setForm({
      ...emptyForm,
      date_achat: purchaseDate,
      date_echeance:
        addOneYearToLocalDateTime(purchaseDate),
    });

    setExpiryManuallyChanged(false);
  }, [open, reglement]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (
      !open ||
      !selectedUserId ||
      selectedClubLoaded ||
      typeof onLoadUserClub !== "function"
    ) {
      return undefined;
    }

    let isMounted = true;

    const loadClub = async () => {
      setClubLoading(true);

      try {
        await onLoadUserClub(selectedUserId);
      } catch (error) {
        console.error(
          "❌ Erreur chargement du club sélectionné :",
          error,
        );
      } finally {
        if (isMounted) {
          setClubLoading(false);
        }
      }
    };

    loadClub();

    return () => {
      isMounted = false;
    };
  }, [
    onLoadUserClub,
    open,
    selectedClubLoaded,
    selectedUserId,
  ]);

  const filteredUsers = useMemo(() => {
    const needle = userSearch
      .trim()
      .toLowerCase();

    const matchingUsers = users.filter((user) => {
      const club =
        clubsByUser[String(user.id)] || null;

      const searchableText = [
        user.prenom,
        user.nom,
        user.email,
        club?.nom_club,
        club?.departement_club,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return (
        !needle ||
        searchableText.includes(needle)
      );
    });

    const selectedUser = users.find(
      (user) =>
        String(user.id) ===
        String(form.user_achat_id),
    );

    if (
      selectedUser &&
      !matchingUsers.some(
        (user) => user.id === selectedUser.id,
      )
    ) {
      return [
        selectedUser,
        ...matchingUsers,
      ];
    }

    return matchingUsers;
  }, [
    clubsByUser,
    form.user_achat_id,
    userSearch,
    users,
  ]);

  if (!open) return null;

  const handleFieldChange = (event) => {
    const { name, value } = event.target;

    setFormError("");

    if (name === "date_echeance") {
      setExpiryManuallyChanged(true);
    }

    setForm((current) => {
      if (
        name === "mode_reglement" &&
        value !== "Stripe"
      ) {
        return {
          ...current,
          mode_reglement: value,
          payment_status: "",
        };
      }

      if (name === "date_achat") {
        return {
          ...current,
          date_achat: value,

          date_echeance: expiryManuallyChanged
            ? current.date_echeance
            : addOneYearToLocalDateTime(value),
        };
      }

      if (name === "devis_url") {
        const cleanedValue = value.trim();

        return {
          ...current,
          devis_url: value,

          devis_statut: !cleanedValue
            ? "non_disponible"
            : current.devis_statut ===
                "non_disponible"
              ? "a_signer"
              : current.devis_statut,
        };
      }

      return {
        ...current,
        [name]: value,
      };
    });
  };

  const handleRecalculateExpiry = () => {
    const calculatedExpiry =
      addOneYearToLocalDateTime(
        form.date_achat,
      );

    if (!calculatedExpiry) {
      setFormError(
        "La date de l’achat doit être renseignée avant de calculer l’échéance.",
      );
      return;
    }

    setForm((current) => ({
      ...current,
      date_echeance: calculatedExpiry,
    }));

    setExpiryManuallyChanged(false);
    setFormError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (typeof onSubmit !== "function") {
      setFormError(
        "La fonction d’enregistrement est indisponible.",
      );
      return;
    }

    const userId = Number(
      form.user_achat_id,
    );

    const amount = Number(
      String(form.prix_total).replace(",", "."),
    );

    const purchaseDate = dateTimeLocalToIso(
      form.date_achat,
    );

    const expiryDate = dateTimeLocalToIso(
      form.date_echeance,
    );

    const devisUrl = form.devis_url.trim();
    const factureUrl =
      form.facture_url.trim();

    if (
      !Number.isInteger(userId) ||
      userId <= 0
    ) {
      setFormError(
        "Sélectionne un utilisateur.",
      );
      return;
    }

    if (!form.formule.trim()) {
      setFormError(
        "La formule est obligatoire.",
      );
      return;
    }

    if (
      !Number.isFinite(amount) ||
      amount < 0
    ) {
      setFormError(
        "Le montant est invalide.",
      );
      return;
    }

    if (!purchaseDate) {
      setFormError(
        "La date de l’achat est invalide.",
      );
      return;
    }

    if (!expiryDate) {
      setFormError(
        "La date d’échéance est obligatoire.",
      );
      return;
    }

    if (
      new Date(expiryDate).getTime() <=
      new Date(purchaseDate).getTime()
    ) {
      setFormError(
        "La date d’échéance doit être postérieure à la date de l’achat.",
      );
      return;
    }

    if (!isValidHttpUrl(devisUrl)) {
      setFormError(
        "Le lien du devis est invalide. Utilise une URL complète ou un chemin commençant par /.",
      );
      return;
    }

    if (!isValidHttpUrl(factureUrl)) {
      setFormError(
        "Le lien de la facture est invalide. Utilise une URL complète ou un chemin commençant par /.",
      );
      return;
    }

    await onSubmit({
      user_achat_id: userId,

      formule: form.formule.trim(),

      prix_total: amount,

      date_achat: purchaseDate,

      date_echeance: expiryDate,

      statut_reglement:
        form.statut_reglement,

      mode_reglement:
        form.mode_reglement,

      payment_status:
        form.mode_reglement === "Stripe" &&
        form.payment_status
          ? form.payment_status
          : null,

      note:
        form.note.trim() || null,

      devis_url:
        devisUrl || null,

      devis_statut:
        devisUrl
          ? form.devis_statut
          : "non_disponible",

      facture_url:
        factureUrl || null,
    });
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/65 p-0 backdrop-blur-sm sm:items-center sm:p-4">
      <button
        type="button"
        aria-label="Fermer la modale"
        className="absolute inset-0 cursor-default"
        onClick={
          saving
            ? undefined
            : onClose
        }
      />

      <div className="relative z-10 flex max-h-[95dvh] w-full flex-col overflow-hidden rounded-t-3xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900 sm:max-w-4xl sm:rounded-3xl">
        <header className="flex items-center justify-between gap-4 border-b border-slate-200 px-4 py-4 dark:border-slate-800 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <div className="rounded-2xl bg-blue-500/10 p-3 text-blue-600 dark:text-blue-300">
              <FaCreditCard />
            </div>

            <div className="min-w-0">
              <h3 className="truncate text-lg font-bold text-slate-900 dark:text-white">
                {isEditing
                  ? "Modifier l’achat"
                  : "Créer un nouvel achat"}
              </h3>

              <p className="text-sm text-slate-500 dark:text-slate-400">
                {isEditing
                  ? `Achat n°${reglement.id}`
                  : "Renouvellement, virement ou achat manuel"}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50 dark:hover:bg-slate-800 dark:hover:text-white"
          >
            <FaTimes />
          </button>
        </header>

        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4 py-5 sm:px-6">
            {formError ? (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300">
                {formError}
              </div>
            ) : null}

            {/* Client et club */}
            <section className="space-y-3">
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-white">
                  Client et club
                </h4>

                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Recherche puis sélectionne le compte
                  propriétaire du club.
                </p>
              </div>

              <div className="relative">
                <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                <input
                  value={userSearch}
                  onChange={(event) =>
                    setUserSearch(
                      event.target.value,
                    )
                  }
                  placeholder="Rechercher par nom, email ou club..."
                  className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </div>

              <select
                name="user_achat_id"
                value={form.user_achat_id}
                onChange={handleFieldChange}
                disabled={
                  usersLoading || saving
                }
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
              >
                <option value="">
                  {usersLoading
                    ? "Chargement des utilisateurs..."
                    : "Sélectionner un utilisateur"}
                </option>

                {filteredUsers.map((user) => {
                  const club =
                    clubsByUser[
                      String(user.id)
                    ] || null;

                  return (
                    <option
                      key={user.id}
                      value={user.id}
                    >
                      {getUserLabel(
                        user,
                        club,
                      )}
                    </option>
                  );
                })}
              </select>

              {selectedUserId > 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-950/50">
                  {clubLoading ||
                  !selectedClubLoaded ? (
                    <div className="flex items-center gap-3">
                      <div className="h-11 w-11 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />

                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-48 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />

                        <div className="h-3 w-32 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
                      </div>
                    </div>
                  ) : selectedClub ? (
                    <div className="flex items-start gap-3">
                      <div className="rounded-xl bg-blue-500/10 p-3 text-blue-600 dark:text-blue-300">
                        <FaBuilding />
                      </div>

                      <div className="min-w-0">
                        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                          Club rattaché
                        </div>

                        <div className="mt-1 font-bold text-slate-900 dark:text-white">
                          {
                            selectedClub.nom_club
                          }
                        </div>

                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500 dark:text-slate-400">
                          {selectedClub.departement_club ? (
                            <span className="inline-flex items-center gap-1">
                              <FaMapMarkerAlt />

                              Département{" "}
                              {
                                selectedClub.departement_club
                              }
                            </span>
                          ) : null}

                          {selectedClub.adresse_club ? (
                            <span>
                              {
                                selectedClub.adresse_club
                              }
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-3">
                      <div className="rounded-xl bg-amber-500/10 p-3 text-amber-600 dark:text-amber-300">
                        <FaBuilding />
                      </div>

                      <div>
                        <div className="font-semibold text-slate-900 dark:text-white">
                          Aucun club rattaché
                        </div>

                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                          Cet utilisateur ne possède
                          actuellement aucun club.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </section>

            {/* Abonnement */}
            <section>
              <div className="mb-4 flex items-start gap-3">
                <div className="rounded-xl bg-emerald-500/10 p-3 text-emerald-600 dark:text-emerald-300">
                  <FaCalendarAlt />
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 dark:text-white">
                    Abonnement
                  </h4>

                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Formule, règlement et période de
                    validité.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="sm:col-span-2">
                  <span className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Formule
                  </span>

                  <input
                    name="formule"
                    list="dashboard-formules"
                    value={form.formule}
                    onChange={handleFieldChange}
                    placeholder="Ex. Presence Pro"
                    disabled={saving}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />

                  <datalist id="dashboard-formules">
                    {FORMULE_OPTIONS.map(
                      (formule) => (
                        <option
                          key={formule}
                          value={formule}
                        />
                      ),
                    )}
                  </datalist>
                </label>

                <label>
                  <span className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Montant
                  </span>

                  <div className="relative">
                    <input
                      type="number"
                      name="prix_total"
                      value={form.prix_total}
                      onChange={handleFieldChange}
                      min="0"
                      step="0.01"
                      placeholder="30.00"
                      disabled={saving}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 pr-10 text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    />

                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-500">
                      €
                    </span>
                  </div>
                </label>

                <label>
                  <span className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Date de l’achat
                  </span>

                  <input
                    type="datetime-local"
                    name="date_achat"
                    value={form.date_achat}
                    onChange={handleFieldChange}
                    disabled={saving}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </label>

                <div className="sm:col-span-2">
                  <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                      Date d’échéance
                    </span>

                    <button
                      type="button"
                      onClick={
                        handleRecalculateExpiry
                      }
                      disabled={saving}
                      className="text-xs font-semibold text-blue-600 transition hover:text-blue-500 disabled:opacity-50 dark:text-blue-300"
                    >
                      Recalculer à +1 an
                    </button>
                  </div>

                  <input
                    type="datetime-local"
                    name="date_echeance"
                    value={form.date_echeance}
                    onChange={handleFieldChange}
                    disabled={saving}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />

                  <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                    Cette date est utilisée pour la
                    jauge et les relances du client.
                  </p>
                </div>

                <label>
                  <span className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Statut du règlement
                  </span>

                  <select
                    name="statut_reglement"
                    value={
                      form.statut_reglement
                    }
                    onChange={handleFieldChange}
                    disabled={saving}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  >
                    {REGLEMENT_STATUS_OPTIONS.map(
                      (status) => (
                        <option
                          key={status}
                          value={status}
                        >
                          {status}
                        </option>
                      ),
                    )}
                  </select>
                </label>

                <label>
                  <span className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Mode de règlement
                  </span>

                  <select
                    name="mode_reglement"
                    value={form.mode_reglement}
                    onChange={handleFieldChange}
                    disabled={saving}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  >
                    {PAYMENT_METHOD_OPTIONS.map(
                      (paymentMethod) => (
                        <option
                          key={paymentMethod}
                          value={paymentMethod}
                        >
                          {paymentMethod}
                        </option>
                      ),
                    )}
                  </select>
                </label>

                <label className="sm:col-span-2">
                  <span className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Statut Stripe
                  </span>

                  <select
                    name="payment_status"
                    value={form.payment_status}
                    onChange={handleFieldChange}
                    disabled={
                      saving ||
                      form.mode_reglement !==
                        "Stripe"
                    }
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:disabled:bg-slate-800"
                  >
                    {PAYMENT_STATUS_OPTIONS.map(
                      (paymentStatus) => (
                        <option
                          key={
                            paymentStatus.value ||
                            "empty-status"
                          }
                          value={
                            paymentStatus.value
                          }
                        >
                          {
                            paymentStatus.label
                          }
                        </option>
                      ),
                    )}
                  </select>
                </label>

                <label className="sm:col-span-2">
                  <span className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Note administrative
                  </span>

                  <textarea
                    name="note"
                    value={form.note}
                    onChange={handleFieldChange}
                    rows={3}
                    disabled={saving}
                    placeholder="Ex. Virement reçu, renouvellement saison 2026-2027..."
                    className="w-full resize-y rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </label>
              </div>
            </section>

            {/* Documents */}
            <section>
              <div className="mb-4 flex items-start gap-3">
                <div className="rounded-xl bg-indigo-500/10 p-3 text-indigo-600 dark:text-indigo-300">
                  <FaFileAlt />
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 dark:text-white">
                    Documents du client
                  </h4>

                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Liens vers le devis et la facture
                    rattachés à cet achat.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="sm:col-span-2">
                  <span className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Lien du devis
                  </span>

                  <div className="relative">
                    <FaLink className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                    <input
                      type="text"
                      name="devis_url"
                      value={form.devis_url}
                      onChange={handleFieldChange}
                      disabled={saving}
                      placeholder="/uploads/devis/devis-123.pdf ou https://..."
                      className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    />
                  </div>

                  {form.devis_url &&
                  isValidHttpUrl(
                    form.devis_url.trim(),
                  ) ? (
                    <a
                      href={form.devis_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex text-xs font-semibold text-blue-600 hover:underline dark:text-blue-300"
                    >
                      Ouvrir le devis
                    </a>
                  ) : null}
                </label>

                <label className="sm:col-span-2">
                  <span className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Statut du devis
                  </span>

                  <select
                    name="devis_statut"
                    value={form.devis_statut}
                    onChange={handleFieldChange}
                    disabled={
                      saving ||
                      !form.devis_url.trim()
                    }
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:disabled:bg-slate-800"
                  >
                    {DEVIS_STATUS_OPTIONS.map(
                      (status) => (
                        <option
                          key={status.value}
                          value={status.value}
                        >
                          {status.label}
                        </option>
                      ),
                    )}
                  </select>
                </label>

                <label className="sm:col-span-2">
                  <span className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Lien de la facture finale
                  </span>

                  <div className="relative">
                    <FaLink className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                    <input
                      type="text"
                      name="facture_url"
                      value={form.facture_url}
                      onChange={handleFieldChange}
                      disabled={saving}
                      placeholder="/uploads/factures/facture-123.pdf ou https://..."
                      className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                    />
                  </div>

                  {form.facture_url &&
                  isValidHttpUrl(
                    form.facture_url.trim(),
                  ) ? (
                    <a
                      href={form.facture_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex text-xs font-semibold text-emerald-600 hover:underline dark:text-emerald-300"
                    >
                      Ouvrir la facture
                    </a>
                  ) : null}
                </label>
              </div>
            </section>
          </div>

          <footer className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50 px-4 py-4 dark:border-slate-800 dark:bg-slate-900 sm:flex-row sm:justify-end sm:px-6">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
            >
              Annuler
            </button>

            <button
              type="submit"
              disabled={
                saving || usersLoading
              }
              className="rounded-xl bg-blue-600 px-5 py-2.5 font-semibold text-white shadow-sm transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving
                ? "Enregistrement..."
                : isEditing
                  ? "Enregistrer les modifications"
                  : "Créer l’achat"}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}